import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Avatar, Empty, Space, Switch, Typography } from 'antd'
import { HomeOutlined } from '@ant-design/icons'
import { IntegrationIcons } from '../integration-icons'
import {
  EmployeeRoles,
  InviteButton,
  StatusDot,
  employeeInitials,
  flattenOrganizations,
  inviteExceptionLabel,
  sortActiveFirst,
} from './shared'
import type { CompanyStructureViewProps, Employee, Office, Organization } from './types'

const { Text } = Typography

/**
 * V1 — Дерево-строки.
 * Иерархия через отступы и левую границу. Статус = точка/приглушение, без дубля тегом.
 * Фильтр «только активные» — логично прячет неактивные ветки целиком.
 */
export function CompanyStructureV1Tree({ structure, onInvite }: CompanyStructureViewProps) {
  const [activeOnly, setActiveOnly] = useState(true)
  const orgs = useMemo(() => flattenOrganizations(structure), [structure])

  const visibleOrgs = orgs.filter(o => (activeOnly ? o.is_active : true))

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <Text type="secondary" style={{ fontSize: 12 }}>
          Статус виден точкой и приглушением — без тега «Неактивен»
        </Text>
        <Space>
          <Text style={{ fontSize: 12 }}>Только активные</Text>
          <Switch size="small" checked={activeOnly} onChange={setActiveOnly} />
        </Space>
      </div>

      {visibleOrgs.length === 0 ? (
        <Empty description="Нет организаций" />
      ) : (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          {visibleOrgs.map(org => (
            <OrgTree key={org.id} org={org} onInvite={onInvite} activeOnly={activeOnly} />
          ))}
        </Space>
      )}
    </div>
  )
}

function OrgTree({
  org,
  onInvite,
  activeOnly,
}: {
  org: Organization
  onInvite: CompanyStructureViewProps['onInvite']
  activeOnly: boolean
}) {
  const offices = sortActiveFirst(org.offices).filter(o => (activeOnly ? o.is_active : true))

  return (
    <div
      style={{
        borderLeft: `3px solid ${org.is_active ? '#1677ff' : '#d9d9d9'}`,
        paddingLeft: 12,
        opacity: org.is_active ? 1 : 0.5,
      }}
    >
      <Space size={8} style={{ marginBottom: 8 }}>
        <StatusDot active={org.is_active} />
        <Link to={`/new/organization/${org.id}`}>
          <Text strong>{org.name}</Text>
        </Link>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {offices.length} офис.
        </Text>
      </Space>

      {offices.length === 0 ? (
        <Text type="secondary" style={{ fontSize: 12 }}>
          Нет офисов · <Link to={`/office/${org.id}/new`}>добавить</Link>
        </Text>
      ) : (
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          {offices.map(office => (
            <OfficeTree
              key={office.id}
              org={org}
              office={office}
              onInvite={onInvite}
              activeOnly={activeOnly}
            />
          ))}
        </Space>
      )}
    </div>
  )
}

function OfficeTree({
  org,
  office,
  onInvite,
  activeOnly,
}: {
  org: Organization
  office: Office
  onInvite: CompanyStructureViewProps['onInvite']
  activeOnly: boolean
}) {
  const employees = sortActiveFirst(office.employees).filter(e =>
    activeOnly
      ? e.is_active || e.invite_status === 'pending' || e.invite_status === 'expired'
      : true,
  )

  return (
    <div
      style={{
        marginLeft: 8,
        borderLeft: '2px solid #f0f0f0',
        paddingLeft: 12,
        opacity: office.is_active ? 1 : 0.5,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 8,
          marginBottom: 6,
          flexWrap: 'wrap',
        }}
      >
        <Space size={8} wrap>
          <HomeOutlined style={{ color: '#8c8c8c' }} />
          <StatusDot active={office.is_active} />
          <Link to={`/office/${office.id}`}>
            <Text strong style={{ fontSize: 13 }}>
              {office.name}
            </Text>
          </Link>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {employees.length}
          </Text>
          <IntegrationIcons integrations={office.integrations} size={14} />
        </Space>
        {office.is_active && <InviteButton org={org} office={office} onInvite={onInvite} />}
      </div>

      {employees.length === 0 ? (
        <Text type="secondary" style={{ fontSize: 12 }}>
          Сотрудников нет
        </Text>
      ) : (
        <div>
          {employees.map((emp, idx) => (
            <EmployeeRow key={emp.id} employee={emp} divider={idx < employees.length - 1} />
          ))}
        </div>
      )}
    </div>
  )
}

function EmployeeRow({ employee, divider }: { employee: Employee; divider: boolean }) {
  const exception = inviteExceptionLabel(employee)
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '8px 0',
        borderBottom: divider ? '1px solid #f5f5f5' : undefined,
        opacity: employee.is_active || exception ? 1 : 0.45,
      }}
    >
      <Avatar size={28} style={{ background: '#1677ff', flexShrink: 0 }}>
        {employeeInitials(employee)}
      </Avatar>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Space size={8} wrap>
          <Link to={`/employees_new/${employee.id}`}>
            <Text style={{ fontSize: 13 }}>{employee.name || employee.login}</Text>
          </Link>
          {exception}
          <IntegrationIcons integrations={employee.integrations} size={12} services={['mango']} />
        </Space>
        <div style={{ marginTop: 2 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {employee.login}
          </Text>
        </div>
        <div style={{ marginTop: 4 }}>
          <EmployeeRoles roles={employee.roles} />
        </div>
      </div>
    </div>
  )
}
